// react
import { ReactNode, useContext, useMemo, useState } from 'react';

// types
import { ActionButton, Entity, Project } from '../../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18next
import { useTranslation } from 'react-i18next';

// context
import {
	ContextEntities,
	ContextProject,
	ContextUserProjectPermissions,
} from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent } from '../../../ui/Filters/Filters';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardEntity } from '../../../cards/CardEntity/CardEntity';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// icons
import { AccountBalanceOutlined as IconAccountBalance } from '@mui/icons-material';

// utils
import { getOptionsEntityTypes } from '../../../../utils/options';

// config
import { featureFlags } from '../../../../config/featureFlags';

// props
export interface TableEntitiesProps {
	project: Project;
}

export const TableEntities = ({ project }: TableEntitiesProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramQ = searchParams.get('q');
	const paramType = searchParams.get('type');

	// defaults
	const defaultFiltersValues = {
		type: 'all',
	};

	// state
	const [filtersValues, setFiltersValues] = useState({
		type: paramType || defaultFiltersValues.type,
	});

	// context
	const { entities, isLoadingEntities, paginationEntities, refetchEntities } =
		useContext(ContextEntities);
	const { isOnboarded: isProjectOnboarded } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// vars
	const idFormFilters = 'form-entities-filters';

	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 3,
			},
			isSortable: true,
			label: t('name'),
			value: 'name',
		},
		{
			GridProps: {
				lg: 2,
			},
			label: t('wallet'),
			value: 'wallet',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('status'),
			value: 'status',
		},
		featureFlags['UTILIZATION']
			? {
					GridProps: {
						lg: 1.5,
					},
					label: t('fundUtilization'),
					tooltip: {
						title: t('tooltip.utilization'),
					},
					value: 'utilization',
				}
			: undefined,
		{
			GridProps: {
				lg: 1,
			},
			label: t('transactionsEvidenced'),
			tooltip: {
				title: t('tooltip.transactionsEvidenced'),
			},
			value: 'traceability',
		},
	].filter(Boolean) as TableHeaderProps[]; // Filter out undefined headers

	// Add financial traceability column conditionally based on feature flag
	if (featureFlags['FINANCIAL_TRACEABILITY']) {
		headers.push({
			GridProps: {
				lg: 1,
			},
			label: t('financialValueEvidenced'),
			tooltip: {
				title: t('tooltip.financialValueEvidenced'),
			},
			value: 'traceability',
		});
	}

	// Add updated column
	headers.push({
		GridProps: {
			lg: 1.5,
		},
		label: t('updated'),
		value: 'modified',
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: { children: t('empty.entities.content') },
			IconCircleProps: { icon: (<IconAccountBalance />) as ReactNode },
			title: { children: t('empty.entities.title') },
		};

		if (paramQ || paramType) {
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		} else if (isProjectOnboarded && userProjectPermissions?.entity?.create && emptyProps.actions) {
			emptyProps.actions.push({
				children: t('addEntity'),
				onClick: () => {
					searchParams.set('dialog', 'addEntity');
					setSearchParams(searchParams);
				},
				variant: 'contained' as ActionButton['variant'],
			});
		}

		return emptyProps;
	}, [
		isProjectOnboarded,
		paramQ,
		paramType,
		searchParams,
		setSearchParams,
		t,
		userProjectPermissions,
	]);

	const content = useMemo(() => {
		if (isLoadingEntities || !entities.length) {
			return <CardEmpty empty={empty} isLoading={isLoadingEntities} />;
		}

		return (
			<Grid container spacing={2}>
				{(entities as Entity[]).map((entity, i) => {
					return (
						<Grid item key={`entity-${i}`} xs={12}>
							<CardEntity entity={entity} project={project} refetchEntities={refetchEntities} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, entities, isLoadingEntities, project, refetchEntities]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={true}>
						<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
					</Grid>
					<Grid item xs="auto">
						<BtnFilters
							filters={[
								{
									hasAllOption: true,
									hasNoneOption: false,
									hasSearchParam: true,
									id: `${idFormFilters}-type`,
									label: t('type'),
									name: 'type',
									onChange: handleChangeFilterValue,
									options: getOptionsEntityTypes(t),
									type: 'SELECT',
									value: filtersValues.type,
								},
							]}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={entities.length} pagination={paginationEntities} />
			</Grid>
		</Grid>
	);
};
